<template>
  <div class="container-fluid py-3">
    <ToastNotification ref="toastComponent" />
    <div class="row">
      <DashboardCard
        title="Time Overview"
        icon="bi-clock-history"
        section="timeOverview"
        :isLoading="['time', 'all'].includes(isLoading)"
        @refresh="refreshData('time')"
      >
        <template #content>
          <GenericList
            :data="timeData"
            :isLoading="['time', 'all'].includes(isLoading)"
            :skeletonCount="6"
            :getItemClass="
              (item) => getTimeBorderClass(item?.offTime, [600, 10800])
            "
          >
            <template #item="{ item }">
              <div class="row">
                <div class="col-md-8 col-sm-12 align-items-start">
                  <div>
                    <strong>{{ item.id }} {{ item.name }}</strong>
                  </div>
                  <span class="text-muted">
                    <i class="bi bi-lightning"></i> ACT: {{ item.act }}
                  </span>
                </div>
                <div class="col-md-4 flex-column align-items-end">
                  <div>
                    <span class="text-muted" title="Last seen">
                      <i class="bi bi-eye"></i> {{ item.offTime }}
                    </span>
                  </div>
                  <div>
                    <span class="text-muted" title="Off time">
                      <i class="bi bi-clock"></i> {{ item.lastSeen }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </GenericList>
        </template>
      </DashboardCard>

      <DashboardCard
        title="Photos"
        icon="bi-images"
        section="photosSection"
        :isLoading="isPhotosLoading"
        @refresh="refreshData('photos')"
      >
        <template #content>
          <GenericList
            :data="photosData"
            :isLoading="isPhotosLoading"
            :skeletonCount="7"
            :getItemClass="
              (item) =>
                'border-' +
                getBadgeClass(item.count, [1, 10]).replace('bg-', '')
            "
          >
            <template #item="{ item }">
              <div
                class="d-flex justify-content-between align-items-center w-100"
              >
                <span><strong>{{ item.id }} {{ item.name }}</strong></span>
                <span :class="getBadgeClass(item.count, [1, 10])" class="badge">
                  {{ item.count }}
                </span>
              </div>
            </template>
          </GenericList>
        </template>
      </DashboardCard>

      <DashboardCard
        title="Paper"
        icon="bi-file-text"
        section="paperSection"
        :isLoading="['paper', 'all'].includes(isLoading)"
        @refresh="refreshData('paper')"
      >
        <template #content>
          <GenericList
            :data="paperData"
            :isLoading="['paper', 'all'].includes(isLoading)"
            :skeletonCount="7"
            :getItemClass="
              (item) =>
                'border-' +
                getBadgeClass(item.count, [10, 50]).replace('bg-', '')
            "
          >
            <template #item="{ item }">
              <div
                class="d-flex justify-content-between align-items-center w-100"
              >
                <span><strong>{{ item.id }} {{ item.name }}</strong></span>
                <span
                  :class="getBadgeClass(item.count, [10, 50])"
                  class="badge"
                >
                  {{ item.count }}
                </span>
              </div>
            </template>
          </GenericList>
        </template>
      </DashboardCard>
    </div>

    <div class="row">
      <div class="col-12">
        <DashboardCard
          title="Messages"
          icon="bi-journal-text"
          section="messagesSection"
          :isLoading="['messages', 'all'].includes(isLoading)"
          :isLarge="true"
          @refresh="refreshData('messages')"
        >
          <template #content>
            <MessageList
              :data="messagesData"
              :isLoading="['messages', 'all'].includes(isLoading)"
              @delete="confirmDelete"
            />
          </template>
        </DashboardCard>
      </div>
    </div>

    <BootstrapModal
        ref="deleteConfirmModal"
        modalId="deleteMessageModal"
        title="Confirm Delete"
        confirmText="Delete"
        @confirm="deleteMessage"
      >
        <template #body>
          Are you sure you want to delete this message?
        </template>
      </BootstrapModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import emitter from "../eventBus";
import ToastNotification from "../components/Common/ToastNotification.vue";
import DashboardService from "@/services/dashboardService.js";
import DashboardCard from "@/components/Common/DashboardCard.vue";
import GenericList from "@/components/Common/GenericList.vue";
import MessageList from "@/components/Common/MessageList.vue";
import MessageService from "@/services/messageService.js";
import BootstrapModal from "@/components/Common/BootstrapModal.vue";

export default {
  components: {
    BootstrapModal,
    DashboardCard,
    ToastNotification,
    GenericList,
    MessageList,
  },
  data() {
    return {
      isLoading: false,
      dashboardData: null,
      worker: null,
      timeData: null,
      photosData: null,
      paperData: null,
      messagesData: null,
      messageToDelete: null,
    };
  },
  computed: {
    ...mapGetters("cards", ["isCardCollapsed"]),
    isPhotosLoading() {
      return ["photos", "all"].includes(this.isLoading);
    },
    isTimeLoading() {
      return ["time", "all"].includes(this.isLoading);
    },
    isPaperLoading() {
      return ["paper", "all"].includes(this.isLoading);
    },
    isMessagesLoading() {
      return ["messages", "all"].includes(this.isLoading);
    },
  },
  mounted() {
    this.isLoading = "all";

    if (window.Worker) {
      let token = localStorage.getItem("token");
      const apiUrl = process.env.VUE_APP_API_BASE_URL;
      const apiInterval =
        Number(process.env.VUE_APP_DASHBOARD_WORKER_TIMING) || 30000;

      this.worker = new Worker(
        new URL("../workers/dashboardWorker.js", import.meta.url)
      );
      this.worker.postMessage({ apiUrl, interval: apiInterval, token });

      this.worker.onmessage = (event) => {
        if (event.data.showToast) {
          emitter.emit("show-toast", {
            message: event.data.error,
            type: "danger",
          });
        }

        if (event.data.refreshToken) {
          token = event.data.refreshToken;
          localStorage.setItem("token", token);
          this.worker.postMessage({ apiUrl, interval: apiInterval, token });
        }

        if (event.data.logout) {
          this.handleLogout();
        }

        if (event.data.success) {
          this.dashboardData = event.data.data.data;
          this.timeData = this.dashboardData.timeOverview;
          this.photosData = this.dashboardData.photosOverview;
          this.paperData = this.dashboardData.paperOverview;
          this.messagesData = this.dashboardData.logOverview;

          this.$nextTick(() => {
            this.isLoading = "";
          });
        } else {
          console.error("Dashboard Worker Error:", event.data.error);
        }
      };
    }
  },
  beforeUnmount() {
    if (this.worker) {
      this.worker.terminate();
    }
  },
  methods: {
    ...mapActions("cards", ["toggleCard"]),
    async handleLogout() {
      await this.$store.dispatch("auth/logout");
      this.$router.push({ name: "LoginPage" });
    },
    confirmDelete(msg) {
      this.messageToDelete = msg;
      this.$refs.deleteConfirmModal.show();
    },

    async deleteMessage() {
      try {
        if (!this.messageToDelete || !this.messageToDelete.id) return;
        const success = await MessageService.deleteMessage(this.messageToDelete.id);
        if (success) {
            this.$refs.deleteConfirmModal.hide();

            emitter.emit("show-toast", {
                message: `Message deleted successfully.`,
                type: "success",
            });
        }
        this.refreshData("messages");
        this.messageToDelete = null;
      } catch (error) {
        console.error("Failed to delete message:", error);
      }
    },
    async refreshData(type) {
      this.isLoading = type;

      try {
        switch (type) {
          case "time":
            this.timeData = await DashboardService.getTimeOverview();
            break;
          case "photos":
            this.photosData = await DashboardService.getPhotosOverview();
            break;
          case "paper":
            this.paperData = await DashboardService.getPaperOverview();
            break;
          case "messages":
            this.messagesData = await DashboardService.getLogOverview();
            break;
        }
      } catch (error) {
        console.error(`Error fetching ${type} data:`, error);
        emitter.emit("show-toast", {
          message:
            error.customMessage || this.$t("errors.failed_to_fetch_data"),
          type: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },
    getTimeBorderClass(offTime, limits = [600, 10800, 999999]) {
      if (!offTime) return this.getBorderClass(0, limits, true);

      const clean = offTime.trim().replace(",", "");

      let days = 0;
      let timeStr = "";

      const match = clean.match(/(?:(\d+)\s+days\s+)?(\d{1,2}:\d{2}:\d{2})/);
      if (match) {
        days = parseInt(match[1] || "0", 10);
        timeStr = match[2];
      }

      const [hours = 0, minutes = 0, seconds = 0] = timeStr.split(":").map(x => parseInt(x, 10) || 0);
      const totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;

      return this.getBorderClass(totalSeconds, limits, true);
    },
    getBorderClass(value, limits, reverse = false) {
      const [low = 600, mid = 10800, high = 999999] = limits;

      if (reverse) {
        if (value <= low) return "border-success";
        if (value > low && value <= mid) return 'border-warning';
        return "border-danger";
      } else {
        if (value <= low) return "border-danger";
        if (value >= mid && value < high) return "border-warning";
        return "border-success";
      }
    },
    getBadgeClass(value, [mid, high]) {
      if (value < mid) return "bg-danger";
      if (value >= mid && value < high) return "bg-warning";
      if (value >= high) return "bg-success";
    },
  },
};
</script>

<style scoped>
.card-header h5 {
  position: relative;
  top: 4px;
}
</style>
